import { useState, type ReactNode } from 'react'
import { api } from '@/api/client'
import { Icon } from '@/components/Icon'
import { fmtBytes, KIND_LABEL } from '@/lib/format'
import { saveUrl } from '@/lib/zip'
import { RecordingPlayer } from './RecordingPlayer'
import { filesChanged, type FileChanges, type RemovedFile, type ReviewDetail } from './types'

const kindLabel = (kind: string) => KIND_LABEL[kind] ?? kind.replaceAll('_', ' ')

/**
 * The frozen execution recording and files for this review, not the author's current uploads.
 * With `changes`, files are marked against an earlier round like the text diffs: new files in
 * green, re-uploads with their old and new size, and removed files struck through in red.
 */
export function PeerExecution({
  base,
  submission,
  active,
  example,
  changes,
  since = 'since last review',
}: {
  example?: { recordingUrl: string }
  base: string
  submission: ReviewDetail['submission']
  active: boolean
  changes?: FileChanges | null
  since?: string
}) {
  const recordings = submission.files.filter((file) => file.kind === 'video')
  const files = submission.files.filter((file) => file.kind !== 'video')
  const added = new Set(changes?.added ?? [])
  const replaced = new Map((changes?.replaced ?? []).map((file) => [file.file_id, file]))
  const removed = changes?.removed ?? []
  const removedRecordings = removed.filter((file) => file.kind === 'video')
  const removedFiles = removed.filter((file) => file.kind !== 'video')
  const [recording, setRecording] = useState(recordings[0]?.file_id ?? '')
  const [error, setError] = useState('')
  const link = async (id: string) => (await api.get<{ url: string }>(`${base}/files/${id}`)).url
  async function download(id: string) {
    if (example) return
    setError('')
    try {
      const { url } = await api.get<{ url: string }>(`${base}/files/${id}`)
      saveUrl(url)
    } catch (err) {
      setError((err as Error).message)
    }
  }
  const name = (fileId: string, filename: string): ReactNode => added.has(fileId) ? <ins>{filename}</ins> : filename
  const size = (fileId: string, bytes: number): ReactNode => {
    const before = replaced.get(fileId)
    if (!before) return fmtBytes(bytes)
    return before.size !== bytes
      ? <><del>{fmtBytes(before.size)}</del> <ins>{fmtBytes(bytes)}</ins> · re-uploaded</>
      : <>{fmtBytes(bytes)} · re-uploaded</>
  }
  const removedFile = (file: RemovedFile) => (
    <li key={`removed-${file.file_id}`} className="is-removed">
      <Icon name="file" size={18} />
      <div>
        <del className="peer-file-name">{file.filename}</del>
        <p className="muted small"><del>{kindLabel(file.kind)} · {fmtBytes(file.size)}</del></p>
      </div>
    </li>
  )
  return (
    <div className={`peer-execution${changes ? ' diff' : ''}`}>
      <div className="panel-section-head">
        <h3>Execution recording</h3>
        {recording && (
          <button className="link small" disabled={!!example} aria-label={`Download ${recordings.find((file) => file.file_id === recording)?.filename ?? 'the recording'}`} onClick={() => void download(recording)}>
            Download
          </button>
        )}
      </div>
      {(recordings.length > 1 || removedRecordings.length > 0 || recordings.some((file) => added.has(file.file_id) || replaced.has(file.file_id))) && (
        <div className="row">
          {recordings.map((file) => (
            <button
              className="ghost small"
              key={file.file_id}
              aria-pressed={recording === file.file_id}
              onClick={() => { setRecording(file.file_id); setError('') }}
            >
              {name(file.file_id, file.filename)}{replaced.has(file.file_id) && <span className="muted"> · re-uploaded</span>}
            </button>
          ))}
          {removedRecordings.map((file) => (
            // The same button as a recording, so it reads evenly, but it cannot be played.
            <button key={file.file_id} type="button" className="ghost small removed-recording" disabled
              aria-label={`${file.filename}, removed`}><del>{file.filename}</del></button>
          ))}
        </div>
      )}
      {!recording ? (
        <p className="muted small">No recording was submitted.</p>
      ) : example ? (
        <RecordingPlayer src={example.recordingUrl} active={active} />
      ) : (
        <RecordingPlayer key={`${base}/${recording}`} load={() => link(recording)} onDownload={() => void download(recording)} active={active} />
      )}
      {error && (
        <p className="note err" role="alert">
          {error}
        </p>
      )}
      {submission.task.submission?.notes && (
        <section className="peer-execution-notes">
          <h3>Execution notes</h3>
          <p className="prewrap">{submission.task.submission.notes}</p>
        </section>
      )}
      <section className="peer-execution-files">
        <h3>Submission files</h3>
        {changes && !filesChanged(changes) && <p className="muted small">Files didn’t change {since}.</p>}
        {submission.task.submission?.empty_start && (
          <p className="muted small">This task starts from an empty document.</p>
        )}
        {!files.length && !removedFiles.length && <p className="muted small">No additional files.</p>}
        <ul className="peer-file-list">
          {files.map((file) => (
            <li key={file.file_id} className={added.has(file.file_id) ? 'is-added' : undefined}>
              <Icon name="file" size={18} />
              <div>
                <button className="link" disabled={!!example} onClick={() => void download(file.file_id)}>
                  {name(file.file_id, file.filename)}
                </button>
                <p className="muted small">
                  {kindLabel(file.kind)} · {size(file.file_id, file.size)}
                </p>
              </div>
            </li>
          ))}
          {removedFiles.map(removedFile)}
        </ul>
      </section>
    </div>
  )
}
